/**
 * `quantity.ts` — the single chokepoint for measurement values.
 *
 * Per **ADR-0007 §1/§3**, a measurement fact stores the **canonical base
 * value of its dimension** (mirroring how `game_date` stores a canonical
 * day count, ADR-0006), and a unit is a pure **affine bijection** onto that
 * base:
 *
 *     canonical = quantity × to_base + offset
 *     quantity  = (canonical − offset) / to_base
 *
 * (`offset ≠ 0` only for affine scales like °C/°F; ratio scales use
 * `offset = 0`.) `convert(a → b)` always routes through the canonical
 * value, so cross-unit comparison/aggregation is a comparison of canonical
 * values.
 *
 * The deciding constraint (ADR-0007 §3) is **exactness**: one dimension can
 * span ~26 orders of magnitude (ångström → light-year) past float64's
 * ~15–16 significant digits, and currency in binary float is categorically
 * wrong. So this module owns an **arbitrary-precision decimal** built on
 * `bigint` (no binary float anywhere), JSON-serialized as a string, and an
 * exact **rational** representation used for the affine arithmetic. All
 * quantity math and (de)serialization route through here — the same
 * discipline `instant.ts` gives the canonical day axis — so a later
 * representation change (e.g. scaled integers for a hot path) is a
 * single-module swap.
 *
 * **Exactness contract.** A {@link Decimal} stores any base-10 value
 * exactly. Affine factors are exact {@link Rat} rationals (so °F's `5/9`
 * slope is exact, not a truncated decimal). Conversion is computed entirely
 * in rationals and rendered to a decimal exactly once, at the end:
 *
 *   - When the rational result terminates in base 10 (every ratio scale —
 *     length / mass / time / currency), the rendered decimal is **exact**
 *     and round-trips losslessly.
 *   - When it does not (e.g. 100 °F → 37.7̅ °C), it is rounded half-even to
 *     {@link DEFAULT_DECIMAL_SCALE} fractional digits — the single, isolated
 *     place any rounding can occur.
 *
 * Currency uses integer minor units (a decimal with scale 0), so monetary
 * canonical values are always exact integers.
 *
 * Everything here is pure and JSON-native (a canonical value travels as a
 * decimal string).
 */

// ---------- Decimal: arbitrary-precision base-10 value ----------

/**
 * An exact base-10 value: `unscaled × 10^(−scale)`, with `scale ≥ 0`. The
 * sign lives in `unscaled`. Always held in **minimal-scale canonical form**
 * (no trailing fractional zeros) so structurally-equal values have equal
 * `unscaled`/`scale` and an equal {@link decimalToString}. Construct one
 * only via {@link decimal} / {@link parseDecimal} / the arithmetic helpers —
 * never by hand, so the representation stays owned by this module.
 */
export interface Decimal {
  readonly unscaled: bigint;
  readonly scale: number;
}

/** The additive identity, `0`. */
export const DECIMAL_ZERO: Decimal = { unscaled: 0n, scale: 0 };

/**
 * Default fractional-digit precision when a conversion produces a
 * non-terminating decimal (the only place rounding happens; ADR-0007 §3).
 * 40 digits comfortably exceeds float64's ~15–16 and any realistic display
 * need while keeping `bigint` cheap.
 */
export const DEFAULT_DECIMAL_SCALE = 40;

/** Matches a plain decimal string: optional sign, digits, optional fraction. */
const DECIMAL_RE = /^[+-]?(?:\d+\.?\d*|\.\d+)$/;
/** Matches a decimal string that may also carry a base-10 exponent. */
const DECIMAL_EXP_RE = /^([+-]?)(\d*)(?:\.(\d*))?(?:[eE]([+-]?\d+))?$/;

/** Normalise `(unscaled, scale)` into minimal-scale canonical form. */
function makeDecimal(unscaled: bigint, scale: number): Decimal {
  if (scale < 0) {
    unscaled *= 10n ** BigInt(-scale);
    scale = 0;
  }
  // Strip trailing fractional zeros so equal values share one representation.
  while (scale > 0 && unscaled % 10n === 0n) {
    unscaled /= 10n;
    scale -= 1;
  }
  return { unscaled, scale };
}

/**
 * Construct a {@link Decimal} from an `unscaled` integer and a non-negative
 * `scale` (`value = unscaled × 10^(−scale)`). The low-level constructor;
 * most callers want {@link parseDecimal}.
 */
export function decimal(unscaled: bigint, scale = 0): Decimal {
  if (!Number.isInteger(scale)) {
    throw new RangeError(`Decimal scale must be an integer, got ${scale}`);
  }
  return makeDecimal(unscaled, scale);
}

/**
 * Is `s` a well-formed decimal string (no exponent)? This is the wire shape
 * a stored `canonical_value` must satisfy. Exponential notation is accepted
 * by {@link parseDecimal} (for authoring convenience) but is not the
 * canonical wire form.
 */
export function isDecimalString(s: unknown): s is string {
  return typeof s === 'string' && s.trim().length > 0 && DECIMAL_RE.test(s.trim());
}

/**
 * Parse a decimal string into an exact {@link Decimal}. Accepts an optional
 * sign, an integer and/or fractional part, and an optional base-10 exponent
 * (`1e-10` for ångström-scale factors). Throws on a malformed string — use
 * at every deserialization boundary (stored `canonical_value`, unit factors).
 */
export function parseDecimal(s: string): Decimal {
  const m = DECIMAL_EXP_RE.exec(s.trim());
  if (!m) throw new RangeError(`invalid decimal string: ${JSON.stringify(s)}`);
  const sign = m[1] === '-' ? -1n : 1n;
  const intPart = m[2] ?? '';
  const fracPart = m[3] ?? '';
  if (intPart.length === 0 && fracPart.length === 0) {
    throw new RangeError(`invalid decimal string: ${JSON.stringify(s)}`);
  }
  const exp = m[4] ? Number.parseInt(m[4], 10) : 0;
  const digits = (intPart === '' ? '0' : intPart) + fracPart;
  const unscaled = sign * BigInt(digits);
  // value = unscaled × 10^(−fracLen) × 10^exp  ⇒  scale = fracLen − exp
  return makeDecimal(unscaled, fracPart.length - exp);
}

/**
 * Serialize a {@link Decimal} to its canonical JSON-native string. Minimal
 * scale (no trailing fractional zeros); `0` renders as `"0"`. This is the
 * exact form stored on a fact's `canonical_value`.
 */
export function decimalToString(d: Decimal): string {
  const neg = d.unscaled < 0n;
  const mag = (neg ? -d.unscaled : d.unscaled).toString();
  if (d.scale === 0) return (neg ? '-' : '') + mag;
  const padded = mag.padStart(d.scale + 1, '0');
  const cut = padded.length - d.scale;
  return `${neg ? '-' : ''}${padded.slice(0, cut)}.${padded.slice(cut)}`;
}

/** Best-effort {@link Decimal} from a JS `number` (used only at migration). */
export function decimalFromNumber(n: number): Decimal {
  if (!Number.isFinite(n)) throw new RangeError(`cannot represent ${n} as a Decimal`);
  // `toString` avoids fixed-notation precision loss; it may use exponent form
  // for very large/small magnitudes, which `parseDecimal` handles.
  return parseDecimal(n.toString());
}

/** Numeric value of a {@link Decimal} (lossy for huge magnitudes — display only). */
export function decimalToNumber(d: Decimal): number {
  return Number(decimalToString(d));
}

// ---------- Rat: exact rational (for affine factors + conversion) ----------

/**
 * An exact rational `n / d`, kept normalised: `d > 0` and `gcd(|n|, d) = 1`.
 * Used for unit affine factors (so `5/9` is exact) and as the intermediate
 * type for conversion — never serialized.
 */
export interface Rat {
  readonly n: bigint;
  readonly d: bigint;
}

function absBig(a: bigint): bigint {
  return a < 0n ? -a : a;
}

function gcdBig(a: bigint, b: bigint): bigint {
  a = absBig(a);
  b = absBig(b);
  while (b !== 0n) {
    [a, b] = [b, a % b];
  }
  return a;
}

/** Build a normalised {@link Rat} from a numerator/denominator. */
export function rat(n: bigint, d: bigint): Rat {
  if (d === 0n) throw new RangeError('rational denominator must be non-zero');
  if (d < 0n) {
    n = -n;
    d = -d;
  }
  if (n === 0n) return { n: 0n, d: 1n };
  const g = gcdBig(n, d);
  return { n: n / g, d: d / g };
}

/** Exact {@link Rat} from a {@link Decimal} (`unscaled / 10^scale`). */
export function decimalToRat(x: Decimal): Rat {
  return rat(x.unscaled, 10n ** BigInt(x.scale));
}

export function ratMul(a: Rat, b: Rat): Rat {
  return rat(a.n * b.n, a.d * b.d);
}
export function ratAdd(a: Rat, b: Rat): Rat {
  return rat(a.n * b.d + b.n * a.d, a.d * b.d);
}
export function ratSub(a: Rat, b: Rat): Rat {
  return rat(a.n * b.d - b.n * a.d, a.d * b.d);
}
export function ratDiv(a: Rat, b: Rat): Rat {
  if (b.n === 0n) throw new RangeError('division by zero rational');
  return rat(a.n * b.d, a.d * b.n);
}
/** Sign of `a − b`: `-1`, `0`, or `1`. Exact (no rounding). */
export function ratCompare(a: Rat, b: Rat): number {
  const lhs = a.n * b.d;
  const rhs = b.n * a.d; // both denominators > 0, so cross-multiplication is order-safe
  return lhs < rhs ? -1 : lhs > rhs ? 1 : 0;
}

/**
 * Render a {@link Rat} to a {@link Decimal}, exactly when it terminates in
 * base 10, otherwise rounded **half-even** to `maxScale` fractional digits.
 * The single, isolated rounding site (ADR-0007 §3).
 */
export function ratToDecimal(r: Rat, maxScale: number = DEFAULT_DECIMAL_SCALE): Decimal {
  if (maxScale < 0 || !Number.isInteger(maxScale)) {
    throw new RangeError(`maxScale must be a non-negative integer, got ${maxScale}`);
  }
  const neg = r.n < 0n;
  const num = neg ? -r.n : r.n;
  const den = r.d;
  let unscaled = num / den;
  let rem = num % den;
  let scale = 0;
  while (rem !== 0n && scale < maxScale) {
    rem *= 10n;
    unscaled = unscaled * 10n + rem / den;
    rem %= den;
    scale += 1;
  }
  if (rem !== 0n) {
    // Truncated at maxScale — round half-even on the next digit.
    rem *= 10n;
    const nextDigit = rem / den;
    const after = rem % den;
    const roundUp =
      nextDigit > 5n || (nextDigit === 5n && (after !== 0n || unscaled % 2n === 1n));
    if (roundUp) unscaled += 1n;
  }
  return makeDecimal(neg ? -unscaled : unscaled, scale);
}

// ---------- factor parsing + affine maps ----------

/**
 * Parse a unit conversion factor into an exact {@link Rat}. Accepts either a
 * decimal string (`"0.0254"`, the common case) or a `"n/d"` fraction
 * (`"5/9"` — how a non-terminating affine slope like °F stays exact). Both
 * sides of a fraction may themselves be decimals (`"1.5/9"`).
 */
export function parseFactor(s: string): Rat {
  const trimmed = s.trim();
  const slash = trimmed.indexOf('/');
  if (slash >= 0) {
    const num = decimalToRat(parseDecimal(trimmed.slice(0, slash)));
    const den = decimalToRat(parseDecimal(trimmed.slice(slash + 1)));
    return ratDiv(num, den);
  }
  return decimalToRat(parseDecimal(trimmed));
}

/**
 * A unit's affine bijection onto its dimension's canonical base value:
 * `canonical = quantity × toBase + offset`. Both factors are exact rationals.
 */
export interface AffineMap {
  readonly toBase: Rat;
  readonly offset: Rat;
}

/**
 * Build an {@link AffineMap} from factor strings. `toBaseStr` must be
 * non-zero (a unit with a zero scale is not invertible); `offsetStr`
 * defaults to `"0"` (the ratio-scale case).
 */
export function affineMap(toBaseStr: string, offsetStr?: string): AffineMap {
  const toBase = parseFactor(toBaseStr);
  if (toBase.n === 0n) throw new RangeError('unit to_base must be non-zero');
  return { toBase, offset: offsetStr === undefined ? { n: 0n, d: 1n } : parseFactor(offsetStr) };
}

// ---------- the conversion pipeline (canonical ↔ display) ----------

/**
 * Map a quantity expressed in a unit to its dimension's canonical base value.
 * `canonical = quantity × toBase + offset`, computed exactly in rationals and
 * rendered once.
 */
export function toCanonical(
  quantity: Decimal,
  map: AffineMap,
  maxScale: number = DEFAULT_DECIMAL_SCALE,
): Decimal {
  const canon = ratAdd(ratMul(decimalToRat(quantity), map.toBase), map.offset);
  return ratToDecimal(canon, maxScale);
}

/**
 * Map a canonical base value back to a quantity in a unit.
 * `quantity = (canonical − offset) / toBase`, computed exactly in rationals
 * and rendered once.
 */
export function fromCanonical(
  canonical: Decimal,
  map: AffineMap,
  maxScale: number = DEFAULT_DECIMAL_SCALE,
): Decimal {
  const q = ratDiv(ratSub(decimalToRat(canonical), map.offset), map.toBase);
  return ratToDecimal(q, maxScale);
}

/**
 * Convert a quantity from one unit to another within the same dimension. The
 * whole pipeline (`from`-quantity → canonical → `to`-quantity) stays rational
 * and is rendered to a decimal exactly once, so terminating results are exact.
 */
export function convert(
  value: Decimal,
  from: AffineMap,
  to: AffineMap,
  maxScale: number = DEFAULT_DECIMAL_SCALE,
): Decimal {
  const canon = ratAdd(ratMul(decimalToRat(value), from.toBase), from.offset);
  const out = ratDiv(ratSub(canon, to.offset), to.toBase);
  return ratToDecimal(out, maxScale);
}

/** Compare two canonical values (or any two decimals): `-1` / `0` / `1`. Exact. */
export function compareDecimal(a: Decimal, b: Decimal): number {
  return ratCompare(decimalToRat(a), decimalToRat(b));
}

/** Structural value equality of two decimals (scale-insensitive). */
export function decimalEquals(a: Decimal, b: Decimal): boolean {
  return a.unscaled === b.unscaled && a.scale === b.scale;
}
